/**
 * Sonidos de notificación — un catálogo, y un sonido POR TIPO DE AVISO.
 *
 * El problema que resuelve. Hasta ahora había una sola preferencia (`notif_sound`) que
 * usaban TODOS los avisos: ticket nuevo, cliente esperando respuesta, chat interno,
 * tarea de campo. Sonaba lo mismo para todo, así que el sonido avisaba que algo pasó
 * pero no QUÉ pasó — y entonces hay que ir a mirar igual, que es exactamente el
 * problema que veníamos atacando con los tickets. Thiago lo propuso después de
 * entrevistar a Ariel y a Santi ("que los ticket se notifiquen como los whatsapp pero
 * con otro sonido para diferenciarlo") y Andy pidió poder elegir el de cada cosa.
 *
 * Un bug que se lleva puesto de paso: el catálogo estaba duplicado en Layout.js y en
 * ChatContext.js, y la copia del chat NO tenía 'ping'. Quien eligiera Ping se quedaba
 * sin sonido en el chat y con sonido en el resto, sin ningún indicio de por qué. Copiar
 * una lista es exactamente así como se rompe.
 *
 * Por qué en localStorage y no en el usuario: el sonido es una propiedad del
 * DISPOSITIVO, no de la cuenta. En la oficina querés que suene y en el celular a la
 * noche no, y es la misma persona. Guardarlo en el server obligaría a la misma
 * elección en los dos lados.
 */

export const NOTIFICATION_SOUNDS = [
  { id: 'none', i18n: 'sounds.names.none', label: 'Sin sonido', group: 'off' },

  // ── Sintetizados. No son archivos: se generan con osciladores en el momento.
  // Es la única forma honesta de tener sonidos "tipo macOS" sin usar los de Apple,
  // que son suyos. Además pesan cero y no hay que servir un .wav por cada uno.
  { id: 'drop',    i18n: 'sounds.names.drop',    label: 'Gota',      group: 'soft', synth: 'drop' },
  { id: 'glass',   i18n: 'sounds.names.glass',   label: 'Cristal',   group: 'soft', synth: 'glass' },
  { id: 'rise',    i18n: 'sounds.names.rise',    label: 'Ascenso',   group: 'soft', synth: 'rise' },
  { id: 'marimba', i18n: 'sounds.names.marimba', label: 'Marimba',   group: 'soft', synth: 'marimba' },
  { id: 'ambient', i18n: 'sounds.names.ambient', label: 'Ambient',   group: 'soft', synth: 'ambient' },
  { id: 'tick',    i18n: 'sounds.names.tick',    label: 'Toque',     group: 'soft', synth: 'tick' },
  { id: 'wa-chirp',i18n: 'sounds.names.chirp',   label: 'Chirp',     group: 'soft', synth: 'chirp' },

  // ── Los de siempre. Se quedan: alguien los eligió y sacárselos sería cambiarle
  // el sonido sin avisarle.
  { id: 'win95-ding',   label: 'Win95 Ding',   group: 'retro', file: '/sounds/win95-ding.wav' },
  { id: 'win95-chime',  label: 'Win95 Chime',  group: 'retro', file: '/sounds/win95-chime.wav' },
  { id: 'win98-notify', label: 'Win98 Notify', group: 'retro', file: '/sounds/win98-notify.wav' },
  { id: 'win98-tada',   label: 'Win98 Tada',   group: 'retro', file: '/sounds/win98-tada.wav' },
  { id: 'ping',         label: 'Ping',         group: 'retro', file: '/sounds/ping.wav' },
];

export const SOUND_GROUPS = [
  { key: 'soft',  i18n: 'sounds.groups.soft' },
  { key: 'retro', i18n: 'sounds.groups.retro' },
];

/**
 * Los avisos que suenan. `defaultSound` está elegido para que de fábrica ya se
 * distingan entre sí: si todos arrancaran igual, la función no serviría hasta que
 * alguien la configure, y nadie configura lo que no sabe que existe.
 */
export const SOUND_EVENTS = [
  // Los defaults se eligen para que se distingan ENTRE SÍ, no sólo para que suenen
  // lindos: el que más urge (un cliente esperando) es el más brillante, y el de fondo
  // (otros avisos) el más discreto.
  { key: 'ticketAttention', i18n: 'sounds.events.ticketAttention', defaultSound: 'glass' },
  { key: 'ticket',          i18n: 'sounds.events.ticket',          defaultSound: 'drop' },
  { key: 'whatsapp',        i18n: 'sounds.events.whatsapp',        defaultSound: 'wa-chirp' },
  { key: 'chat',            i18n: 'sounds.events.chat',            defaultSound: 'marimba' },
  { key: 'errand',          i18n: 'sounds.events.errand',          defaultSound: 'rise' },
  { key: 'billing',         i18n: 'sounds.events.billing',         defaultSound: 'ambient' },
  { key: 'system',          i18n: 'sounds.events.system',          defaultSound: 'tick' },
];

const STORAGE_KEY = 'notif_sounds_v1';
const LEGACY_KEY = 'notif_sound';

function readMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) || {};
  } catch { /* modo privado */ }
  return {};
}

/**
 * El sonido elegido para un aviso.
 *
 * Migración de la preferencia vieja: si alguien había puesto 'none' para silenciar
 * todo, se respeta en TODOS los eventos — silenciar era una decisión deliberada y
 * volver a hacer ruido sin avisar sería lo peor que podríamos hacerle. Cualquier otra
 * elección vieja se aplica sólo a los tickets (que es lo que sonaba más) y el resto
 * toma los defaults nuevos, para que la separación se note desde el primer día.
 */
export function getSoundFor(eventKey) {
  const map = readMap();
  if (map[eventKey] !== undefined) return map[eventKey];

  let legacy = null;
  try { legacy = localStorage.getItem(LEGACY_KEY); } catch { /* ignore */ }
  if (legacy === 'none') return 'none';
  if (legacy && eventKey === 'ticket') return legacy;

  return SOUND_EVENTS.find(e => e.key === eventKey)?.defaultSound || 'none';
}

export function setSoundFor(eventKey, soundId) {
  const map = readMap();
  map[eventKey] = soundId;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

// Los <audio> se cachean para que la repetición sea instantánea.
const audioCache = {};
let synthCtx = null;

function audioCtx() {
  if (!synthCtx) synthCtx = new (window.AudioContext || window.webkitAudioContext)();
  // Chrome suspende el contexto hasta que hay un gesto del usuario; si quedó suspendido
  // de un intento anterior, reanudarlo acá evita el silencio mudo del segundo aviso.
  if (synthCtx.state === 'suspended') synthCtx.resume().catch(() => {});
  return synthCtx;
}

// Volumen general. Los avisos compiten con música y llamadas: tienen que llamar la
// atención sin sobresaltar. Todo el diseño de abajo asume esta escala.
const MASTER = 0.22;

/**
 * Una voz: oscilador + envolvente + pasabajos opcional.
 *
 * La envolvente es exponencial, no lineal, porque el oído percibe el volumen en
 * escala logarítmica: una caída lineal se escucha como un corte seco. El ataque de
 * unos pocos milisegundos evita el "click" de arrancar en volumen pleno, que es lo que
 * hace que un sonido corto suene barato.
 */
function voice(ctx, t0, { freq, freqTo, dur, vol = 1, type = 'sine', attack = 0.006, cutoff }) {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  if (freqTo) o.frequency.exponentialRampToValueAtTime(freqTo, t0 + dur * 0.9);

  const peak = Math.max(0.0002, vol * MASTER);
  // El ataque nunca puede pasar la mitad de la nota: si los dos ramps quedaran fuera de
  // orden la envolvente se rompe y el sonido sale mudo o distorsionado, sin error. Es
  // una red para un futuro retoque de los números, no algo que hoy pase.
  const atk = Math.min(attack, dur * 0.5);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + atk);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  let tail = g;
  if (cutoff) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(cutoff, t0);
    f.Q.setValueAtTime(0.7, t0);
    g.connect(f);
    tail = f;
  }
  o.connect(g);
  tail.connect(ctx.destination);
  o.start(t0);
  o.stop(t0 + dur + 0.03);
}

/**
 * Campana: la fundamental más parciales INARMÓNICOS (2.76, 5.40… no 2, 3, 4).
 * Esa inarmonía es literalmente la diferencia entre "campana" y "flauta": un metal que
 * vibra no produce múltiplos enteros. Los parciales altos duran menos, como en el metal
 * real, y por eso el sonido "se abre" y después queda el cuerpo.
 */
function bell(ctx, t0, base, dur, vol) {
  const partials = [[1, 1, 1], [2.76, 0.5, 0.62], [5.40, 0.28, 0.4], [8.93, 0.14, 0.25]];
  partials.forEach(([mult, v, d]) => {
    voice(ctx, t0, { freq: base * mult, dur: dur * d, vol: vol * v, attack: 0.003 });
  });
}

/** Percutido de madera: cuerpo sinusoidal + un armónico corto que hace de golpe. */
function mallet(ctx, t0, base, dur, vol) {
  voice(ctx, t0, { freq: base, dur, vol, attack: 0.004, cutoff: base * 6 });
  voice(ctx, t0, { freq: base * 4.02, dur: dur * 0.18, vol: vol * 0.35, attack: 0.002 });
  voice(ctx, t0, { freq: base * 9.9, dur: dur * 0.07, vol: vol * 0.14, attack: 0.001 });
}

// Notas usadas. Todo cae en una pentatónica de Do mayor, que es la escala sin
// intervalos disonantes: cualquier combinación suena bien y ninguna suena a alarma.
const N = { C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880, C6: 1046.5, E6: 1318.5, G6: 1568 };

const SYNTHS = {
  // Gota: una nota que cae de tono, filtrada. Corta y neutra — la de uso más frecuente.
  drop: (ctx, t) => {
    // Volumen subido tras la prueba de oído de Thiago (9-sep): "gota y chirp se escuchan
    // muy bajo". El resto los distinguía bien, así que se tocan sólo estos dos. La gota
    // además tiene el filtro bajo en 2600 Hz, que le come presencia: se abre a 3400 para
    // que se oiga sin tener que subirle tanto la ganancia y quede sonando pesada.
    voice(ctx, t, { freq: N.G5, freqTo: N.D5, dur: 0.45, vol: 1.5, cutoff: 3400 });
    voice(ctx, t, { freq: N.G5 * 2, freqTo: N.D5 * 2, dur: 0.18, vol: 0.35 });
  },
  // Cristal: campana aguda y brillante. Es el default del aviso más urgente porque es
  // el que corta mejor sobre el ruido de fondo de una oficina.
  glass: (ctx, t) => {
    bell(ctx, t, N.E6, 1.1, 0.55);
    bell(ctx, t + 0.055, N.G6, 0.85, 0.3);
  },
  // Ascenso: dos notas subiendo una tercera. "Algo empieza", sin urgencia.
  rise: (ctx, t) => {
    voice(ctx, t, { freq: N.C5, dur: 0.3, vol: 0.75, type: 'triangle', cutoff: 3000 });
    voice(ctx, t + 0.11, { freq: N.E5, dur: 0.42, vol: 0.8, type: 'triangle', cutoff: 3200 });
  },
  // Marimba: dos notas de madera. Cálido, no molesta repetido, ideal para el chat.
  marimba: (ctx, t) => {
    mallet(ctx, t, N.D5, 0.5, 0.85);
    mallet(ctx, t + 0.12, N.A5, 0.6, 0.6);
  },
  // Ambient: acorde con ataque lento y filtro que se abre. No sobresalta: aparece.
  // Va en facturas y pagos, que importan pero nunca son urgentes al segundo.
  ambient: (ctx, t) => {
    [N.C5, N.E5, N.G5].forEach((f, i) => {
      voice(ctx, t + i * 0.04, { freq: f, dur: 1.6, vol: 0.32, type: 'triangle', attack: 0.28, cutoff: 1500 + i * 400 });
    });
    voice(ctx, t + 0.2, { freq: N.C6, dur: 1.3, vol: 0.14, attack: 0.4 });
  },
  // Toque: lo más discreto posible sin ser inaudible. Para el ruido de fondo.
  tick: (ctx, t) => {
    voice(ctx, t, { freq: N.A5, dur: 0.11, vol: 0.5, cutoff: 3000 });
  },
  // El chirp de dos tonos que la burbuja de WhatsApp ya hacía. Se conserva igual.
  chirp: (ctx, t) => {
    // Idem: era el volumen original de la burbuja de WhatsApp y Thiago lo escuchó bajo.
    // Los dos tonos son cortos (0,12 y 0,2 s), así que suben sin volverse molestos.
    voice(ctx, t, { freq: 880, dur: 0.13, vol: 2.0, attack: 0.004 });
    voice(ctx, t + 0.15, { freq: 1175, dur: 0.22, vol: 2.4, attack: 0.004 });
  },
};

function playSynth(name) {
  try {
    const fn = SYNTHS[name];
    if (!fn) return;
    const ctx = audioCtx();
    fn(ctx, ctx.currentTime + 0.01);
  } catch { /* no crítico: un aviso nunca puede romper la pantalla */ }
}

/** Reproduce un sonido del catálogo por id. */
export function playSound(soundId) {
  if (!soundId || soundId === 'none') return;
  const sound = NOTIFICATION_SOUNDS.find(s => s.id === soundId);
  if (!sound) return;
  if (sound.synth) return playSynth(sound.synth);
  if (!sound.file) return;
  try {
    if (!audioCache[soundId]) audioCache[soundId] = new Audio(sound.file);
    const audio = audioCache[soundId];
    audio.currentTime = 0;
    audio.play().catch(() => {});   // el navegador puede bloquear autoplay
  } catch { /* no crítico */ }
}

/** Reproduce el sonido configurado para un tipo de aviso. */
export function playFor(eventKey) {
  playSound(getSoundFor(eventKey));
}

export default { NOTIFICATION_SOUNDS, SOUND_EVENTS, getSoundFor, setSoundFor, playSound, playFor };
